import { useParams } from "#routers/hooks";
import WorkerInputOutputDetails from "#src/batteries-included-components/KeyValueList/WorkerInputOutputDetails/WorkerInputOutputDetails";
import { RoutingLink } from "#src/batteries-included-components/RoutingLink";
import { useStorageKey } from "#src/hooks/useStorageKey";
import { useGetOneUser } from "#src/components/hooks/adapters/useUsers";
import {
  useGetOneWorker,
  useGetOneWorkerRun,
  useGetOneWorkerVersion,
} from "#src/components/hooks/adapters/useWorkers";
import { WorkerDetailRoutePath } from "#src/routes/workers/definitions/[workerId]/detail";
import { useBreadcrumbsFromRoute } from "#src/utils/route";
import {
  formatWorkerRunDuration,
  getWorkerRunIdDisplayLabel,
  WorkerRunStatusToPillVariantMapping,
} from "#src/view-models/workers";
import {
  DataTable,
  EmptyState,
  generateDocumentTitle,
  KeyValuePanel,
  Page,
  Panel,
  Pill,
  TextDataDisplay,
} from "@validereinc/common-components";
import { WorkerRunStatus } from "@validereinc/domain";
import { toTitleCaseString } from "@validereinc/utilities";
import React, { useMemo } from "react";
import { Titled } from "react-titled";
import { WorkerRunDetailRoutePath } from ".";
import { WorkerRunsRoutePath } from "..";
import { WorkerRunReportsTablePanel } from "./WorkerRunReportsTablePanel";

/**
 * Full-page view for a single worker run (`GET /worker_runs/:id`).
 */
export const WorkerRunDetailPage = () => {
  const { runId } = useParams<{ runId: string }>();
  const [
    workerRunReportTableConfigStorageKey,
    workerRunReportTableStateStorageKey,
  ] = useStorageKey(
    ["table-configuration", "table-state"],
    runId ? `worker-run-detail-reports-${runId}` : "worker-run-detail-reports"
  );

  const {
    data: runResponse,
    isLoading: isLoadingRun,
    isError: isRunError,
  } = useGetOneWorkerRun({ id: runId ?? "" }, { enabled: Boolean(runId) });
  const run = runResponse?.data;

  const { data: workerResponse } = useGetOneWorker(
    { id: run?.worker_id ?? "" },
    { enabled: Boolean(run?.worker_id) }
  );
  const worker = workerResponse?.data;

  const { data: workerVersion } = useGetOneWorkerVersion(
    {
      workerId: run?.worker_id ?? "",
      version: run?.version ?? 0,
    },
    {
      enabled:
        Boolean(run?.worker_id) &&
        run?.version != null &&
        !Number.isNaN(run.version),
    }
  );

  const { data: creatorUser, isLoading: isLoadingCreator } = useGetOneUser({
    id: run?.created_by ?? null,
  });

  const [breadcrumbs] = useBreadcrumbsFromRoute(
    WorkerRunDetailRoutePath,
    {
      "/:runId": {
        title: run
          ? `Worker Run ${getWorkerRunIdDisplayLabel(run.id)}`
          : undefined,
      },
    },
    Boolean(runId)
  );

  const overviewData = useMemo(() => {
    if (run == null) return [];
    const workerLink = (
      <RoutingLink
        to={WorkerDetailRoutePath.toLinkParts({
          pathParams: { workerId: run.worker_id },
        })}
      >
        {worker?.name ?? run.worker_id}
      </RoutingLink>
    );
    const versionLink = (
      <RoutingLink
        to={WorkerDetailRoutePath.toLinkParts({
          pathParams: {
            workerId: run.worker_id,
          },
          queryParams: { version: String(run.version) },
        })}
      >
        {run.version}
      </RoutingLink>
    );
    return [
      {
        title: "Worker",
        value: workerLink,
      },
      {
        title: "Version",
        value: versionLink,
      },
      {
        title: "Status",
        value: (
          <Pill variant={WorkerRunStatusToPillVariantMapping[run.status]}>
            {toTitleCaseString(run.status, { intent: "ui" })}
          </Pill>
        ),
      },
      ...(run.status === WorkerRunStatus.FAILED && run.error_message
        ? [
            {
              title: "Error",
              value: run.error_message,
            },
          ]
        : []),
      {
        title: "Started at",
        value: (
          <DataTable.DataRow.DateCell
            value={run.started_at}
            withTime
          />
        ),
      },
      {
        title: "Finished at",
        value:
          run.finished_at != null && run.finished_at !== "" ? (
            <DataTable.DataRow.DateCell
              value={run.finished_at}
              withTime
            />
          ) : (
            "—"
          ),
      },
      {
        title: "Duration",
        value: formatWorkerRunDuration(run),
      },
      {
        title: "Created at",
        value: (
          <DataTable.DataRow.DateCell
            value={run.created_at}
            withTime
          />
        ),
      },
      {
        title: "Created by",
        value: (
          <TextDataDisplay
            value={creatorUser?.name ?? "—"}
            isLoading={Boolean(run.created_by) && isLoadingCreator}
          />
        ),
      },
    ];
  }, [run, worker?.name, creatorUser?.name, isLoadingCreator]);

  const isLoading = Boolean(runId) && isLoadingRun;

  const documentTitlePageName =
    run != null
      ? `Run ${getWorkerRunIdDisplayLabel(run.id)}`
      : WorkerRunDetailRoutePath.title;

  return (
    <Titled
      title={generateDocumentTitle(
        documentTitlePageName,
        WorkerRunsRoutePath.title
      )}
    >
      <Page
        title={
          run != null ? (
            <>
              Worker Run {getWorkerRunIdDisplayLabel(run.id)}
              <Pill
                variant={WorkerRunStatusToPillVariantMapping[run.status]}
                hasDot={false}
              >
                {toTitleCaseString(run.status, { intent: "ui" })}
              </Pill>
            </>
          ) : (
            WorkerRunDetailRoutePath.title
          )
        }
        category={WorkerRunsRoutePath.title}
        breadcrumbs={breadcrumbs}
        isLoading={isLoading}
      >
        {isRunError && !isLoadingRun ? (
          <Panel title="Worker run">
            <EmptyState
              title="Run not found"
              suggestion="This worker run does not exist or you may not have access to it."
            />
          </Panel>
        ) : null}
        {run != null && !isRunError ? (
          <>
            <KeyValuePanel
              data={overviewData}
              panelKeyValueListProps={{ variant: "shaded", color: "neutral" }}
              panelProps={{
                title: "Overview",
                isFluidY: true,
                loaded: true,
              }}
            />

            <WorkerInputOutputDetails
              type="input"
              workerRun={run}
              workerVersion={workerVersion}
            />
            <WorkerInputOutputDetails
              type="output"
              workerRun={run}
              workerVersion={workerVersion}
            />
            <WorkerRunReportsTablePanel
              runId={run.id}
              tableConfigStorageKey={workerRunReportTableConfigStorageKey}
              tableStateStorageKey={workerRunReportTableStateStorageKey}
            />
          </>
        ) : null}
      </Page>
    </Titled>
  );
};
